import {FactureeTheme} from '../FactureeComponents/FactureeTheme';
import React, {useState} from 'react';
import '../App.css';
import {createTheme, ThemeProvider} from '@mui/material/styles';
import {Button, Stack, Typography, Divider} from "@mui/material";
import {PaletteMode} from "@mui/material";
import ShowCode from "../helpers/ShowCode.js";


/**
 * Import Facturee sample Inputs
 */
import FactureeButton from "../standard-mui-examples/inputs/facturee-button";
import FactureeButtonGroup from "../standard-mui-examples/inputs/facturee-buttongroup";
import FactureeTextfield from "../standard-mui-examples/inputs/facturee-textfield";
import FactureeAutocomplete from "../standard-mui-examples/inputs/facturee-autocomplete";
import FactureeCheckbox from "../standard-mui-examples/inputs/facturee-checkbox";
import FactureeRadiogroup from "../standard-mui-examples/inputs/facturee-radiogroup";
import FactureeFab from "../standard-mui-examples/inputs/facturee-fab";
import FactureeRating from "../standard-mui-examples/inputs/facturee-rating";
import FactureeSwitch from "../standard-mui-examples/inputs/facturee-switch";
import FactureeSlider from "../standard-mui-examples/inputs/facturee-slider";
import FactureeTransferList from "../standard-mui-examples/inputs/facturee-transfer-list";
import FactureeToggleButtons from "../standard-mui-examples/inputs/facturee-toggle-buttons";

/**
 * Import Facturee sample Data Display
 */
import FactureeTypography from "../standard-mui-examples/data-display/facturee-typography";
import FactureeColorGrid from "../standard-mui-examples/data-display/facturee-color-grid";
import FactureeAvatar from "../standard-mui-examples/data-display/facturee-avatar";
import FactureeChip from "../standard-mui-examples/data-display/facturee-chip";
import FactureeList from "../standard-mui-examples/data-display/facturee-list";
import FactureeTableBasic from "../standard-mui-examples/data-display/facturee-table-basic";
import FactureeTableData from "../standard-mui-examples/data-display/facturee-table-data";
import FactureeTableDense from "../standard-mui-examples/data-display/facturee-table-dense";
import FactureeTooltip from "../standard-mui-examples/data-display/facturee-tooltip";

/**
 * Import Facturee sample Feedback
 */
import FactureeAlert from "../standard-mui-examples/feedback/facturee-alert";
import FactureeBackdrop from "../standard-mui-examples/feedback/facturee-backdrop";
import FactureeDialog from "../standard-mui-examples/feedback/facturee-dialog";
import FactureeProgress from "../standard-mui-examples/feedback/facturee-progress";
import FactureeProgressDeterminate from "../standard-mui-examples/feedback/facturee-progress-determinate";
import FactureeProgressLabel from "../standard-mui-examples/feedback/facturee-progress-label";
import FactureeSnackbar from "../standard-mui-examples/feedback/facturee-snackbar";

/**
 * Import Facturee sample Surfaces
 */
import FactureeBackground from "../standard-mui-examples/surfaces/facturee-background";
import FactureeAccordion from "../standard-mui-examples/surfaces/facturee-accordion";
import FactureeAppBar from "../standard-mui-examples/surfaces/facturee-app-bar";
import FactureeCard from "../standard-mui-examples/surfaces/facturee-card";
import FactureePaper from "../standard-mui-examples/surfaces/facturee-paper";

/**
 * Import Facturee sample Navigations
 */
import FactureeBottomNav from "../standard-mui-examples/navigation/facturee-bottom-nav";
import FactureeBreadcrumbs from "../standard-mui-examples/navigation/facturee-breadcrumbs";
import FactureeDrawer from "../standard-mui-examples/navigation/facturee-drawer";
import FactureeLink from "../standard-mui-examples/navigation/facturee-link";
import FactureeMenu from "../standard-mui-examples/navigation/facturee-menu";
import FactureePagination from "../standard-mui-examples/navigation/facturee-pagination";
import FactureeSpeedDial from "../standard-mui-examples/navigation/facturee-speedDial";
import FactureeStepper from "../standard-mui-examples/navigation/facturee-stepper";
import FactureeTabs from "../standard-mui-examples/navigation/facturee-tabs";

//console.log(FactureeTheme('light'));

function FactureeMuiElements() {
  const [mode, setMode] = useState<PaletteMode>('light');
  const theme = createTheme(FactureeTheme(mode))
  return (
      <div className="App">

        <ThemeProvider theme={theme}>

          <FactureeBackground>

            <Typography variant="h1">Facturee Corporate Design</Typography>
            <Typography variant="h1">React/MUI Styled Elements — Typescript Version</Typography>

            <Stack spacing={2} direction="row" sx={{mt: 2, lineHeight: 1.3}}>
              <Button variant="outlined" size="large"
                      onClick={() => setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'))}>
                {/*<Icon sx={{height: '1rem'}} color="inherit">
                {theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
              </Icon>*/}
                Toggle Color Mode
              </Button>
            </Stack>

            {/* ---- INPUT ELEMENTS ---- */}

            <Typography variant="h2" sx={{mt: 5}}>1. Basics</Typography>

            <Divider><Typography variant="h4">Typography</Typography></Divider>
            <FactureeTypography/>

            <Divider><Typography variant="h4">Colors</Typography></Divider>
            <FactureeColorGrid/>

            <Typography variant="h2" sx={{mt: 5}}>2. Input Elements</Typography>

            <Divider><Typography variant="h4">Buttons</Typography></Divider>
            <FactureeButton/>

            <Divider><Typography variant="h4">Button Group</Typography></Divider>
            <FactureeButtonGroup/>

            <Divider><Typography variant="h4">Floating Action Buttons</Typography></Divider>
            <FactureeFab/>

            <Divider><Typography variant="h4">Text field (including Select & Multiline)</Typography></Divider>
            <FactureeTextfield/>

            <Divider><Typography variant="h4">Autocomplete</Typography></Divider>
            <FactureeAutocomplete/>

            <Divider><Typography variant="h4">Checkboxes</Typography></Divider>
            <FactureeCheckbox/>

            <Divider><Typography variant="h4">Radio Group</Typography></Divider>
            <FactureeRadiogroup/>

            <Divider><Typography variant="h4">Switch</Typography></Divider>
            <FactureeSwitch/>

            <Divider><Typography variant="h4">Slider</Typography></Divider>
            <FactureeSlider/>

            <Divider><Typography variant="h4">Rating (Stack)</Typography></Divider>
            <FactureeRating/>

            <Divider><Typography variant="h4">Transfer List</Typography></Divider>
            <FactureeTransferList/>

            <Divider><Typography variant="h4">Toggle Buttons</Typography></Divider>
            <FactureeToggleButtons/>

            {/*---- Data Display ----*/}

            <Typography variant="h2" sx={{mt: 10}}>3. Data Display</Typography>

            <Divider><Typography variant="h4">Avatar (Initials)</Typography></Divider>
            <FactureeAvatar/>

            <Divider><Typography variant="h4">Chip</Typography></Divider>
            <FactureeChip/>

            <Divider><Typography variant="h4">List</Typography></Divider>
            <FactureeList/>


            <Divider><Typography variant="h4">Table</Typography></Divider>
            <FactureeTableBasic/>
            <FactureeTableDense/>
            {/*<FactureeTableData />*/}
            <FactureeTableData/>

            <Divider><Typography variant="h4">Tooltip</Typography></Divider>
            <FactureeTooltip/>


            {/* ---- Feedback ----*/}
            <Typography variant="h2" sx={{mt: 10}}>4. Feedback</Typography>

            <Divider><Typography variant="h4">Alerts</Typography></Divider>
            <FactureeAlert/>

            <Divider><Typography variant="h4">Backdrop</Typography></Divider>
            <FactureeBackdrop/>

            <Divider><Typography variant="h4">Simple Dialog</Typography></Divider>
            <FactureeDialog/>

            <Divider><Typography variant="h4">Snackbar</Typography></Divider>
            <FactureeSnackbar/>

            <Divider><Typography variant="h4">Progress</Typography></Divider>
            <FactureeProgress/>
            <FactureeProgressDeterminate/>
            <FactureeProgressLabel/>


            {/*---- SURFACES ----*/}

            <Typography variant="h2" sx={{mt: 10}}>5. Surfaces</Typography>

            <Divider><Typography variant="h4">Accordion</Typography></Divider>
            <FactureeAccordion/>

            <Divider><Typography variant="h4">FactureeMuiElements bar</Typography></Divider>
            <FactureeAppBar/>

            <Divider><Typography variant="h4">Card</Typography></Divider>
            <FactureeCard/>

            <Divider><Typography variant="h4">Paper</Typography></Divider>
            <FactureePaper/>

            {/*---- NAVIGATION ----*/}

            <Typography variant="h2" sx={{mt: 10}}>6. Navigation</Typography>

            <Divider><Typography variant="h4">Bottom Navigation</Typography></Divider>
            <FactureeBottomNav/>

            <Divider><Typography variant="h4">Breadcrumb Navigation</Typography></Divider>
            <FactureeBreadcrumbs/>

            <Divider><Typography variant="h4">Drawer</Typography></Divider>
            <FactureeDrawer/>

            <Divider><Typography variant="h4">Basic Link</Typography></Divider>
            <FactureeLink/>

            <Divider><Typography variant="h4">Menu</Typography></Divider>
            <FactureeMenu/>

            <Divider><Typography variant="h4">Pagination</Typography></Divider>
            <FactureePagination/>

            <Divider><Typography variant="h4">Speed Dial</Typography></Divider>
            <FactureeSpeedDial/>

            <Divider><Typography variant="h4">Stepper</Typography></Divider>
            <FactureeStepper/>

            <Divider><Typography variant="h4">Tabs</Typography></Divider>
            <FactureeTabs/>
            <FactureeTabs type="indexCard"/>

          </FactureeBackground>

        </ThemeProvider>

      </div>
  )
}

export default FactureeMuiElements;
